import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Search,
  ThumbsUp,
  Trash2,
  X,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { formatFullDate } from "@/lib/format"
import {
  useAlbumsList,
  useDeleteAlbum,
  type AdminAlbum,
} from "@/lib/queries/albums"
import { ExportButton } from "@/components/ExportButton"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Albums (list)
// LEGACY: apps/admin/src/pages/Album.jsx
// API:    POST /admin/album_list   (server pagination/sort/search)
//         POST /admin/album_delete (delete)
//
// ─── Field parity (legacy ↔ v2) ──────────────────────────────────────
// LIST request body:
//   page          → page          (number)
//   limit         → limit         (number)
//   search        → search        (string)
//   sortyBy       → sortyBy       (string, backend typo PRESERVED)
//   sortDirection → sortDirection (1 | -1)
//
// LIST response columns rendered (same set + same data as legacy):
//   cover_image     → "Cover" (img)
//   title           → "Title" (sortable)
//   category_name   → "Category" (NOT sortable in legacy; preserved)
//   author          → "Author" (sortable)
//   album_type      → "Type" (sortable; "1" → Free, "2"/anything else → Paid)
//   is_carousel     → "Carousel" (sortable; ✓ / ✗)
//   like_count      → "👍 Likes" (sortable)
//   order           → "Order" (sortable)
//   carousel_order  → "Carousel Ord." (sortable)
//   created_at      → "Created" (sortable)
//   _id (action)    → Update link → /albums/:id (was /updateAlbum/:id)
//   _id (action)    → Delete button → POST /admin/album_delete {id}
//
// DELETE confirm payload (POST /admin/album_delete):
//   id              → id
// ───────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

function albumTypeLabel(t: AdminAlbum["album_type"]): string {
  // Legacy renders `value.album_type == "1" ? "Free" : "Paid"` — accept both
  // string "1" and number 1.
  return t === 1 || t === "1" ? "Free" : "Paid"
}

export default function Albums() {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [sortyBy, setSortyBy] = useState<string | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, limit, sortyBy, sortDirection])

  const params = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortyBy,
      sortDirection,
    }),
    [page, limit, debouncedSearch, sortyBy, sortDirection]
  )

  const albumsQ = useAlbumsList(params)
  const deleteMut = useDeleteAlbum()

  const [deleteAlbum, setDeleteAlbum] = useState<AdminAlbum | null>(null)

  const onSortClick = (field: string) => {
    if (sortyBy === field) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1))
    } else {
      setSortyBy(field)
      setSortDirection(-1)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortyBy !== field) return null
    return sortDirection === 1 ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  const totalPages = albumsQ.data?.totalPages ?? 0
  const totalDocs = albumsQ.data?.totalDocs ?? 0

  const handleDeleteConfirm = async () => {
    if (!deleteAlbum) return
    await deleteMut.mutateAsync(deleteAlbum._id)
    setDeleteAlbum(null)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
          <div>
            <CardTitle>Albums</CardTitle>
            <CardDescription>
              {totalDocs > 0
                ? `${totalDocs.toLocaleString()} total`
                : "Manage album collections"}
            </CardDescription>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search albums…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search albums"
              />
            </div>
            <ExportButton<AdminAlbum>
              filenamePrefix="albums"
              totalEstimate={totalDocs}
              columns={[
                { label: "Title", get: (a) => a.title },
                { label: "Category", get: (a) => a.category_name },
                { label: "Author", get: (a) => a.author },
                { label: "Type", get: (a) => albumTypeLabel(a.album_type) },
                { label: "Carousel", get: (a) => (a.is_carousel ? "yes" : "no") },
                { label: "Like Count", get: (a) => a.like_count ?? 0 },
                { label: "Order", get: (a) => a.order },
                { label: "Carousel Order", get: (a) => a.carousel_order },
                { label: "Description", get: (a) => a.description },
                { label: "Cover Image", get: (a) => a.cover_image },
                { label: "Created", get: (a) => a.created_at },
                { label: "ID", get: (a) => a._id },
              ]}
              request={async (page, limit) => {
                const res = await api.post("/admin/album_list", {
                  page,
                  limit,
                  search: debouncedSearch || undefined,
                  sortyBy,
                  sortDirection,
                })
                return {
                  docs: res.data?.data?.docs ?? [],
                  totalDocs: res.data?.data?.totalDocs ?? 0,
                }
              }}
            />
            <Button asChild>
              <Link to="/albums/new">Add album</Link>
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {albumsQ.isError && (
            <div
              role="alert"
              className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load albums. Try refreshing.</span>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Cover</TableHead>
                <SortableTH field="title" label="Title" {...{ onSortClick, SortIcon }} />
                <TableHead>Category</TableHead>
                <SortableTH field="author" label="Author" {...{ onSortClick, SortIcon }} />
                <SortableTH
                  field="album_type"
                  label="Type"
                  className="w-20"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="is_carousel"
                  label="Carousel"
                  className="w-24"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="like_count"
                  label={
                    <span className="flex items-center gap-1">
                      <ThumbsUp className="h-3.5 w-3.5" /> Likes
                    </span>
                  }
                  className="w-24"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="order"
                  label="Order"
                  className="w-20"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="carousel_order"
                  label="Car. Ord."
                  className="w-24"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH field="created_at" label="Created" {...{ onSortClick, SortIcon }} />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {albumsQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={11} className="h-32 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!albumsQ.isLoading && (albumsQ.data?.docs.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="h-32 text-center text-muted-foreground">
                    No albums match your filters.
                  </TableCell>
                </TableRow>
              )}
              {albumsQ.data?.docs.map((a) => (
                <TableRow key={a._id}>
                  <TableCell>
                    {a.cover_image ? (
                      <img
                        src={a.cover_image}
                        alt=""
                        loading="lazy"
                        className="h-16 w-16 rounded object-cover"
                      />
                    ) : (
                      <div className="grid h-16 w-16 place-items-center rounded bg-muted text-xs text-muted-foreground">
                        no img
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-medium">
                    {a.title || "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {a.category_name || "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {a.author || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {albumTypeLabel(a.album_type)}
                  </TableCell>
                  <TableCell>
                    {a.is_carousel ? (
                      <Check className="h-4 w-4 text-green-600" aria-label="Yes" />
                    ) : (
                      <X className="h-4 w-4 text-muted-foreground" aria-label="No" />
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {typeof a.like_count === "number" ? a.like_count : 0}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {typeof a.order === "number" ? a.order : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {typeof a.carousel_order === "number" ? a.carousel_order : "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatFullDate(a.created_at)}
                  </TableCell>
                  <TableCell className="space-x-1 text-right">
                    <Button asChild variant="ghost" size="sm" aria-label="Edit album">
                      <Link to={`/albums/${a._id}`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteAlbum(a)}
                      aria-label="Delete album"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Dialog
        open={!!deleteAlbum}
        onOpenChange={(open) => !open && setDeleteAlbum(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete album?</DialogTitle>
            <DialogDescription>
              {deleteAlbum?.title || "This album"} will be removed. Chapters and
              other linked records may be affected. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteAlbum(null)}
              disabled={deleteMut.isPending}
            >
              Keep album
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete album
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SortableTH({
  field,
  label,
  className,
  onSortClick,
  SortIcon,
}: {
  field: string
  label: React.ReactNode
  className?: string
  onSortClick: (field: string) => void
  SortIcon: React.ComponentType<{ field: string }>
}) {
  return (
    <TableHead className={cn("cursor-pointer select-none", className)}>
      <button
        type="button"
        onClick={() => onSortClick(field)}
        className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIcon field={field} />
      </button>
    </TableHead>
  )
}
